"use client"

import { Button } from "@/components/ui-library/button/Button"
import { cn } from "@/utils/utils"
import Link from "next/link"
import { useState } from "react"
import { ClinicVisitType, ClinicVisitTypeDateGroup } from "./ClinicVisitTypeAvailabilitySection"

type Props = {
  visitType: ClinicVisitType
  isFirst?: boolean
  isLast?: boolean
  showAccordion?: boolean
  visitTypeLink?: string | null
}

export const ClinicVisitTypeAvailability = ({ visitType, isFirst, isLast, showAccordion, visitTypeLink }: Props) => {
  const [isOpen, setIsOpen] = useState(isFirst ?? true)

  const borderRadius =
    isFirst && isLast
      ? "rounded-2xl"
      : isFirst
        ? "rounded-t-2xl rounded-b-none"
        : isLast
          ? "rounded-b-2xl rounded-t-none"
          : "rounded-none"

  const borderEdge = isFirst && isLast ? "border-y-0" : isFirst ? "border-b-0" : isLast ? "border-t-0" : "border-y-0"

  const dateFormatter = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    month: "short",
    day: "2-digit"
  })

  const timeFormatter = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    hour: "2-digit",
    minute: "2-digit"
  })

  const formatTime = (isoString: string) => timeFormatter.format(new Date(isoString))

  return (
    <div
      className={cn(
        "w-full border border-[#F4F4F4] bg-white px-8",
        isFirst && isLast
          ? "py-10"
          : isFirst
            ? "pb-4 pt-10 md:pb-[1.875rem]"
            : isLast
              ? "pb-3 pt-3 md:pb-10 md:pt-[1.875rem]"
              : "pb-3 pt-5 md:py-[1.875rem]",
        borderRadius,
        borderEdge
      )}
    >
      {visitType.title && (
        <div className="mb-4">
          {isFirst && <p className="text-xl font-bold tracking-tight md:hidden">Lediga tider</p>}
          {showAccordion && !isFirst ? (
            <button
              className="flex w-full items-center justify-between text-left md:hidden"
              onClick={() => setIsOpen(o => !o)}
            >
              <span className="text-base font-semibold text-[#1B1B1B]">{visitType.title}</span>
              <svg
                className={cn(
                  "h-auto w-2 flex-shrink-0 transition-transform duration-200",
                  isOpen ? "-rotate-90" : "rotate-90"
                )}
                viewBox="0 0 12 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={cn(
                    "M1.15002 19.1C0.900024 18.85 0.775024 18.554 0.775024 ",
                    "18.212C0.775024 17.8707 0.900024 17.575 1.15002 17.325L8.47502 ",
                    "10L1.12502 2.65C0.891691 2.41667 0.775024 2.125 0.775024 1.775C0.775024 ",
                    "1.425 0.900024 1.125 1.15002 0.875C1.40002 0.625 1.69602 0.5 2.03802 0.5C2.37936 ",
                    "0.5 2.67502 0.625 2.92502 0.875L11.325 9.3C11.425 9.4 11.496 9.50833 11.538 ",
                    "9.625C11.5794 9.74167 11.6 9.86667 11.6 10C11.6 10.1333 11.5794 10.2583 11.538 ",
                    "10.375C11.496 10.4917 11.425 10.6 11.325 10.7L2.90002 19.125C2.66669 19.3583 ",
                    "2.37936 19.475 2.03802 19.475C1.69602 19.475 1.40002 19.35 1.15002 19.1Z"
                  )}
                  fill="currentColor"
                />
              </svg>
            </button>
          ) : (
            <p className="text-base font-semibold text-[#1B1B1B] md:hidden">{visitType.title}</p>
          )}
          <p className="hidden text-xl font-semibold tracking-tight md:block">{visitType.title} - lediga tider</p>
        </div>
      )}

      {/* Accordion content: overflow-hidden on mobile only, always visible on md+ */}
      <div
        className={cn(
          "md:block md:max-h-none md:overflow-visible",
          isOpen || !showAccordion
            ? "max-h-[2000px] overflow-hidden transition-all duration-500"
            : "max-h-0 overflow-hidden transition-all duration-500"
        )}
      >
        {/* Date columns: stacked mobile+small tablet, 2-col iPad (lg:767px), 3-col desktop (min-1238px) */}
        <div
          className={cn(
            "flex flex-col gap-3 md:flex-row md:flex-wrap",
            "md:justify-between md:gap-y-6 min-[1238px]:flex-nowrap",
            "min-[1238px]:gap-x-8"
          )}
        >
          {visitType.dateGroups?.slice(0, 3).map((timeSlot: ClinicVisitTypeDateGroup, idx, arr) => (
            <div
              key={timeSlot.date}
              className={cn(
                "flex flex-col gap-[0.625rem] border-b border-[#E7E7E7] pb-4",
                idx === arr.length - 1 && "border-b-0 pb-0",
                "md:w-[47%] md:border-b-0 md:pb-0",
                "min-[1238px]:w-auto"
              )}
            >
              <span className="text-sm font-semibold">{dateFormatter.format(new Date(timeSlot.date))}</span>
              <div className="min-[1238px]:min-w-[304px]">
                {timeSlot.slots.length === 0 ? (
                  <div className="flex h-[40px] items-center">
                    <span className="text-sm font-bold">Inga lediga tider</span>
                  </div>
                ) : (
                  <div className="flex flex-row justify-between gap-2 min-[1238px]:justify-start">
                    {timeSlot.slots.slice(0, 4).map((slot, index) => {
                      const isOverflow = timeSlot.slots.length > 4 && index === 3
                      const label = isOverflow
                        ? timeSlot.slots.length > 8
                          ? "Se fler"
                          : `+${timeSlot.slots.length - 3} till`
                        : formatTime(slot.start)
                      return (
                        <Button
                          key={slot.id}
                          asChild
                          variant="primary_light"
                          className="h-[40px] min-w-0 rounded-[20px] bg-[#EFF5F4] text-sm font-semibold text-black hover:bg-[#c8deda] sm:w-[70px] sm:flex-[0_1_22.5%] md:w-auto lg:w-[70px] xl:text-sm min-[1238px]:h-10 min-[1238px]:rounded-full"
                        >
                          <Link href={visitTypeLink ?? "#"}>{label}</Link>
                        </Button>
                      )
                    })}
                    {Array.from({ length: Math.max(0, 4 - timeSlot.slots.length) }).map((_, i) => (
                      <Button
                        key={`ph-${i}`}
                        variant="primary_light"
                        className="invisible h-[40px] min-w-0 rounded-[20px] bg-[#EFF5F4] sm:w-[70px] sm:flex-[0_1_22.5%] md:w-auto lg:w-[70px] xl:text-sm min-[1238px]:h-10 min-[1238px]:rounded-full"
                        tabIndex={-1}
                        aria-hidden="true"
                      >
                        --
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {/* Boka tid: 4th column on iPad and desktop, aligned bottom-right */}
          <div className="hidden md:flex md:w-[47%] md:flex-col md:items-end md:justify-end min-[1238px]:w-auto min-[1238px]:flex-1">
            <Button asChild variant="primary" className="rounded-full px-8 py-3 text-base xl:text-base">
              <Link href={visitTypeLink ?? "#"}>Boka tid</Link>
            </Button>
          </div>
        </div>

        {/* Mobile only button: full width, hidden on iPad+ */}
        <div className={cn("mt-10 md:hidden", !isLast && "border-b border-[#E7E7E7]", isFirst ? "pb-4" : "pb-3")}>
          <Button asChild variant="primary" className="w-full rounded-full px-8 py-2 text-base">
            <Link href={visitTypeLink ?? "#"}>Se alla lediga tider och boka</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
